// MarketingGanttPage.jsx — v119.126
// Full-page interactive Gantt across ALL approved marketing campaigns.
// Data source: GET /api/marketing-plans/campaigns  (returns each campaign with
// start_date/end_date/progress_pct/impact_achieved_pct/tasks_done/tasks_total).
//
// Layout: horizontal time axis at top (day/week ticks), one row per
// campaign, bar filled by progress %, tooltip on hover, click → focus
// details drawer.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "./lib/api";
import { completionText, statusText } from "./marketing/campaignDisplay";
import { zVar } from "./lib/zIndex";
import { InfoTip } from "./components/InfoTip";
import { ksaParts, ksaToday } from "./lib/ksaTime";

const _parse = (s) => { if (!s) return null; const d = new Date(s); return isNaN(d) ? null : d; };
const _addDays = (d, n) => new Date(d.getTime() + n * 864e5);
const _diffDays = (a, b) => Math.max(0, Math.round((b - a) / (1000 * 60 * 60 * 24)));
const _fmt = (d) => d ? ksaToday(d) : "—";

const AR_MONTHS = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];

export default function MarketingGanttPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [focused, setFocused] = useState(null);
  const [scale, setScale] = useState("month"); // week | month | quarter

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/marketing-plans/campaigns");
      setCampaigns(r.data?.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر تحميل الحملات");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Compute overall time window
  const window = useMemo(() => {
    const withDates = campaigns.filter((c) => c.start_date && c.end_date);
    if (withDates.length === 0) {
      const t = new Date();
      return { start: t, end: _addDays(t, 90), days: 90 };
    }
    const starts = withDates.map((c) => _parse(c.start_date)).filter(Boolean);
    const ends = withDates.map((c) => _parse(c.end_date)).filter(Boolean);
    let s = new Date(Math.min(...starts.map((d) => d.getTime())));
    let e = new Date(Math.max(...ends.map((d) => d.getTime())));
    // pad 3 days on each side for breathing room
    s = _addDays(s, -3); e = _addDays(e, 3);
    return { start: s, end: e, days: Math.max(1, _diffDays(s, e)) };
  }, [campaigns]);

  const rowsSorted = useMemo(() => {
    return [...campaigns].sort((a, b) => {
      const da = _parse(a.start_date)?.getTime() || Infinity;
      const dbb = _parse(b.start_date)?.getTime() || Infinity;
      return da - dbb;
    });
  }, [campaigns]);

  // Grid tick positions
  const ticks = useMemo(() => {
    const stepDays = scale === "week" ? 7 : scale === "quarter" ? 30 : 14;
    const out = [];
    for (let i = 0; i <= window.days; i += stepDays) {
      const d = _addDays(window.start, i);
      const p = ksaParts(d);
      out.push({ pos: (i / window.days) * 100, label: `${p.date} ${AR_MONTHS[p.month - 1].slice(0, 3)}` });
    }
    return out;
  }, [window, scale]);

  const today = new Date();
  const todayPos = today > window.start && today < window.end
    ? (_diffDays(window.start, today) / window.days) * 100 : null;

  const barFor = (c) => {
    const s = _parse(c.start_date), e = _parse(c.end_date);
    if (!s || !e) return null;
    const left = (_diffDays(window.start, s) / window.days) * 100;
    const width = Math.max(0.5, (_diffDays(s, e) / window.days) * 100);
    return { left, width };
  };

  return (
    <div data-testid="mkt-gantt-page" style={{ padding: 12 }}>
      <div style={{
        padding: "16px 20px", borderRadius: 12, marginBottom: 12,
        background: "linear-gradient(135deg, #3E2723 0%, #5D4037 60%, #C9A227 120%)",
        color: "#FBF7F0", display: "flex", justifyContent: "space-between",
        alignItems: "center", gap: 8, flexWrap: "wrap",
      }}>
        <div>
          <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 700, letterSpacing: 2 }}>FULL-PAGE MARKETING GANTT</div>
          <div style={{ fontSize: 18, fontWeight: 900 }}>🗓 خريطة الحملات الزمنية</div>
          <div style={{ fontSize: 11, opacity: 0.9, marginTop: 4 }}>
            {campaigns.length} حملة · نطاق: {_fmt(window.start)} → {_fmt(window.end)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {["week", "month", "quarter"].map((s) => (
            <button key={s} type="button" onClick={() => setScale(s)}
              data-testid={`mkt-gantt-scale-${s}`}
              style={{ padding: "6px 12px", background: scale === s ? "#C9A227" : "transparent",
                        color: scale === s ? "#3E2723" : "#FBF7F0",
                        border: "1px solid #C9A227", borderRadius: 4, fontSize: 11,
                        fontWeight: 800, cursor: "pointer" }}>
              {s === "week" ? "أسبوع" : s === "month" ? "شهر" : "ربع سنة"}
            </button>
          ))}
          <button type="button" onClick={load}
            data-testid="mkt-gantt-refresh"
            style={{ padding: "6px 12px", background: "#FBF7F0", color: "#3E2723",
                      border: "none", borderRadius: 4, fontSize: 11,
                      fontWeight: 800, cursor: "pointer" }}>
            🔄 تحديث
          </button>
        </div>
      </div>

      {loading && <div className="card" style={{ padding: 20, textAlign: "center" }}>⏳ يحمّل...</div>}
      {err && <div className="card" style={{ padding: 12, color: "#991b1b", background: "#fee2e2" }}>{err}</div>}
      {!loading && !err && campaigns.length === 0 && (
        <div className="card" style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>
          لا توجد حملات معتمدة بعد. اذهب إلى «الخطط التسويقية» واعتمد بنداً لإنشاء أول حملة.
        </div>
      )}

      {!loading && !err && campaigns.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="mkt-gantt-chart">
          {/* Time axis */}
          <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", borderBottom: "2px solid #C9A22740" }}>
            <div style={{ padding: "10px 12px", fontWeight: 800, fontSize: 12,
                            background: "#FBF7F0", color: "var(--brown-deep)" }}>
              الحملة
            </div>
            <div style={{ position: "relative", height: 40, background: "#FBF7F0" }}>
              {ticks.map((t, i) => (
                <div key={i} style={{
                  position: "absolute", left: `${t.pos}%`, top: 0, bottom: 0,
                  borderInlineStart: "1px dashed #C9A22740",
                  paddingInlineStart: 4, fontSize: 10.5, color: "var(--muted)",
                  fontWeight: 700,
                }}>
                  {t.label}
                </div>
              ))}
              {todayPos !== null && (
                <div style={{
                  position: "absolute", left: `${todayPos}%`, top: 0, bottom: 0,
                  borderInlineStart: "2px solid #EF4444", zIndex: zVar('extra_2'),
                }}>
                  {/* تصحيح A3 (البند 1ج) — كان InfoTip=اليوم يكرّر حرفياً
                      الشارة الظاهرة أدناه مباشرة؛ حُذف. */}
                  <div style={{ position: "absolute", top: -2, insetInlineStart: 3,
                                  background: "#EF4444", color: "#fff", fontSize: 9,
                                  padding: "1px 5px", borderRadius: 3, fontWeight: 800 }}>
                    اليوم
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Rows */}
          {rowsSorted.map((c, idx) => {
            const bar = barFor(c);
            const progress = Number(c.progress_pct || 0);
            const impact = c.impact_achieved_pct;
            const sales = Number(c.attributed_sales || 0);
            const notStarted = !!c.not_started;
            const tone = notStarted ? "#9CA3AF"
                       : progress >= 80 ? "#10B981" : progress >= 50 ? "#C9A227" : "#EF4444";
            return (
              <div key={c.id} onClick={() => setFocused(focused?.id === c.id ? null : c)}
                data-testid={`mkt-gantt-row-${c.id}`}
                style={{
                  display: "grid", gridTemplateColumns: "220px 1fr",
                  borderBottom: "1px solid #eee",
                  background: idx % 2 === 0 ? "#fff" : "#FAFAFA",
                  cursor: "pointer",
                }}>
                <div style={{ padding: "10px 12px", fontSize: 12 }}>
                  <div style={{ fontWeight: 800, color: "var(--brown-deep)", overflow: "hidden",
                                  textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.name}
                  </div>
                  <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 2 }}>
                    {c.tasks_done}/{c.tasks_total} · {notStarted ? "تبدأ قريباً" : completionText(c)}
                    {c.is_delayed && !notStarted && " · ⏳ متأخرة"}
                    {c.possible_duplicate && " · ⚠ مكرر"}
                    {impact != null && ` · 🎯 ${impact}%`}
                    {sales > 0 && ` · 💰 ${Math.round(sales).toLocaleString("en-US")} ر.س`}
                  </div>
                </div>
                <div style={{ position: "relative", height: 50 }}>
                  {ticks.map((t, i) => (
                    <div key={i} style={{
                      position: "absolute", left: `${t.pos}%`, top: 0, bottom: 0,
                      borderInlineStart: "1px dashed #eee",
                    }} />
                  ))}
                  {todayPos !== null && (
                    <div style={{ position: "absolute", left: `${todayPos}%`, top: 0, bottom: 0,
                                    borderInlineStart: "2px solid #EF444460" }} />
                  )}
                  {bar && (
 <div 
                      style={{
                        position: "absolute", left: `${bar.left}%`, width: `${bar.width}%`,
                        top: 12, height: 26, borderRadius: 6,
                        background: `${tone}22`, border: `1px solid ${tone}`,
                        display: "flex", alignItems: "center", overflow: "hidden",
                      }}>
                      <div style={{ height: "100%", width: `${Math.min(progress, 100)}%`,
                                     background: tone, transition: "width 400ms" }} ><InfoTip text={`${c.name} · ${c.start_date} → ${c.end_date} · ${notStarted ? "لم تبدأ بعد" : `${progress}% منجز`}`} /></div>
                      <div style={{
                        position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)",
                        color: progress > 30 ? "#fff" : "var(--brown-deep)",
                        fontSize: 10.5, fontWeight: 800, whiteSpace: "nowrap",
                        textShadow: progress > 30 ? "0 1px 2px rgba(0,0,0,0.3)" : "none",
                      }}>
                        {notStarted ? "تبدأ قريباً" : `${progress}%`}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Focus drawer */}
      {focused && (
        <div data-testid="mkt-gantt-focus" className="card" style={{ padding: 16, marginTop: 12,
                                                                             border: "2px solid #C9A227" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontWeight: 900, fontSize: 15, color: "var(--brown-deep)" }}>
              📌 {focused.name}
            </div>
            <button type="button" onClick={() => setFocused(null)}
              data-testid="mkt-gantt-focus-close"
              style={{ background: "transparent", border: "none", fontSize: 18, cursor: "pointer" }}>
              ✕
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                          gap: 8, fontSize: 12 }}>
            <div><b>البداية:</b> {focused.start_date || "—"}</div>
            <div><b>النهاية:</b> {focused.end_date || "—"}</div>
            <div><b>الميزانية:</b> {focused.budget != null ? `${Number(focused.budget).toLocaleString()} ر.س` : "—"}</div>
            <div><b>مهام:</b> {focused.tasks_done}/{focused.tasks_total}</div>
            <div><b>الحالة:</b> {statusText(focused)}</div>
            <div><b>الإنجاز:</b> {completionText(focused)}</div>
            {focused.time_elapsed_pct != null && (
              <div><b>الوقت المنقضي:</b> {focused.time_elapsed_pct}%{focused.is_delayed ? " · ⏳ متأخرة" : ""}</div>
            )}
            <div><b>مبيعات فترة الحملة:</b> {focused.attributed_sales != null ? `${Math.round(Number(focused.attributed_sales)).toLocaleString("en-US")} ر.س` : "—"}</div>
            <div><b>فواتير الفترة:</b> {focused.attributed_invoices != null ? Number(focused.attributed_invoices).toLocaleString("en-US") : "—"}</div>
            {focused.impact_achieved_pct != null && (
              <div><b>الأثر:</b> {focused.impact_achieved_pct}%</div>
            )}
            {focused.owners?.length > 0 && (
              <div style={{ gridColumn: "1 / -1" }}>
                <b>الفريق:</b> {focused.owners.join("، ")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
